<template>
  <div>
    <!-- HEADER -->
    <div class="d-flex justify-content-between align-items-start mb-1">
      <div>
        <h4 class="font-weight-bold">Upcoming Break Expiries</h4>
        <div class="text-muted">
          Users whose self-exclusion breaks expire within the next 30 days
        </div>
      </div>

      <div>
        <b-button
          variant="primary"
          @click="handleExport"
          :disabled="loadingUpcomingBreakExpiries"
        >
          <feather-icon icon="DownloadIcon" size="16" class="mr-1" />
          Export
        </b-button>
      </div>
    </div>

    <!-- FILTERS & STATS -->
    <b-card>
      <div>
        <b-row>
          <b-col cols="12" sm="6">
            <div class="stat-box">
              <div class="text-muted small">Total Expiries</div>
              <div class="h5 font-weight-bold">
                {{ metadata.total || 0 }}
              </div>
            </div>
          </b-col>
          <b-col cols="12" sm="6">
            <div class="stat-box">
              <div class="text-muted small">Next 7 Days</div>
              <div class="h5 font-weight-bold">
                {{
                  upcomingBreakExpiries.filter((x) => daysUntilExpiry(x) <= 7)
                    .length
                }}
              </div>
            </div>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- TABLE -->
    <b-card no-body>
      <div class="m-2">
        <b-row>
          <b-col cols="12" md="6">
            <label>Show</label>
            <v-select
              v-model="perPage"
              :options="perPageOptions"
              :clearable="false"
              class="d-inline-block mx-1"
              style="width: 100px"
            />
            <label>entries</label>
          </b-col>
          <b-col cols="12" md="6" class="text-right">
            <b-form-input
              v-model="search"
              placeholder="Search by email or user ID..."
            />
          </b-col>
        </b-row>
      </div>

      <b-table
        :items="filteredItems"
        :fields="tableFields"
        show-empty
        responsive
        :busy="loadingUpcomingBreakExpiries"
      >
        <template #cell(userId)="{ item }">
          <b-link :to="{ name: 'customers-view', params: { id: item.userId } }">
            {{ item.userId }}
          </b-link>
        </template>

        <template #cell(name)="{ item }">
          {{ item.firstName }} {{ item.lastName }}
        </template>

        <template #cell(breakType)="{ item }">
          <b-badge :variant="item.breakType === 'long' ? 'warning' : 'info'">
            {{ item.breakType === "long" ? "Long Break" : "Short Break" }}
          </b-badge>
        </template>

        <template #cell(expiredAt)="{ item }">
          <div>
            <div class="font-weight-semibold">
              {{ formatDate(item.expiredAt) }}
            </div>
            <div class="small text-muted">
              {{ daysUntilExpiry(item) }} days remaining
            </div>
          </div>
        </template>
      </b-table>

      <div class="mx-2 mb-2 mt-2" v-if="metadata.total > 0">
        <b-row>
          <b-col cols="12" sm="6">
            <span class="text-muted">
              Showing {{ (currentPage - 1) * perPage + 1 }} to
              {{ Math.min(currentPage * perPage, metadata.total) }} of
              {{ metadata.total }} entries
            </span>
          </b-col>
          <b-col cols="12" sm="6" class="d-flex justify-content-end"
            >
            <b-pagination
              v-model="currentPage"
              :total-rows="metadata.total"
              :per-page="perPage"
              class="mb-0 mt-1 mt-sm-0"
              first-number
              last-number
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BLink,
  BBadge,
  BFormInput,
  BPagination,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions, mapState } from "vuex";
import { format, differenceInDays } from "date-fns";
import { formatCurrencyBuiltInFunc } from "@/helpers/utils";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BLink,
    BBadge,
    BFormInput,
    vSelect,
    BPagination,
  },
  data() {
    return {
      currentPage: 1,
      perPage: 25,
      perPageOptions: [10, 25, 50, 100],
      search: "",
      tableFields: [
        { key: "userId", label: "User ID" },
        { key: "email", label: "Email" },
        { key: "name", label: "Name" },
        { key: "breakType", label: "Break Type" },
        { key: "expiredAt", label: "Expires At" },
      ],
      metadata: { total: 0 },
    };
  },
  computed: {
    ...mapState("reporting", [
      "upcomingBreakExpiries",
      "loadingUpcomingBreakExpiries",
      "upcomingBreakExpiriesPagination",
    ]),
    filteredItems() {
      if (!this.search) return this.upcomingBreakExpiries;
      const s = this.search.toLowerCase();
      return this.upcomingBreakExpiries.filter(
        (item) =>
          item.email?.toLowerCase().includes(s) ||
          item.userId?.toLowerCase().includes(s) ||
          item.firstName?.toLowerCase().includes(s) ||
          item.lastName?.toLowerCase().includes(s)
      );
    },
  },
  watch: {
    currentPage() {
      this.fetchData();
    },
    perPage() {
      this.currentPage = 1;
      this.fetchData();
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    ...mapActions("reporting", ["getUpcomingBreakExpiries"]),
    formatCurrencyBuiltInFunc,
    async fetchData() {
      const data = await this.getUpcomingBreakExpiries({
        page: this.currentPage,
        limit: this.perPage,
      });
      if (data?.pagination) {
        this.metadata = {
          total: data.pagination.total || 0,
        };
      }
    },
    formatDate(date) {
      return format(new Date(date), "dd/MM/yyyy");
    },
    daysUntilExpiry(item) {
      return differenceInDays(new Date(item.expiredAt), new Date());
    },
    handleExport() {
      // CSV export functionality can be added
      alert("Export functionality to be implemented");
    },
  },
};
</script>

<style scoped>
.stat-box {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
}
</style>
